import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

interface Checkpoint { key: string; label: string; location: string; at: string | null; reached: boolean; current: boolean }
interface Track {
  reference: string; productTitle: string; brand: string; method: string;
  carrier: string; carrierLabel: string; eta: string; trackingNumber?: string;
  state: string; estimatedDelivery?: string | null; checkpoints: Checkpoint[];
}

const CARRIER_ICON: Record<string, string> = { InPost: '📦', 'Royal Mail': '📮', DPD: '🚚' };
const fmtDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' }) : null);
const fmtTime = (iso: string) => new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/* Quadratic bezier point for the arced route */
function bezier(t: number) {
  const P0 = { x: 55, y: 128 }, P1 = { x: 300, y: 18 }, P2 = { x: 565, y: 92 };
  const mt = 1 - t;
  return {
    x: mt * mt * P0.x + 2 * mt * t * P1.x + t * t * P2.x,
    y: mt * mt * P0.y + 2 * mt * t * P1.y + t * t * P2.y,
  };
}

export default function TrackParcel() {
  const { code = '' } = useParams();
  const { data, isLoading, isError } = useQuery<Track>({
    queryKey: ['public-track', code],
    queryFn: async () => (await api.get(`/orders/track/${code}`)).data,
    retry: false,
  });

  const done = data?.state === 'RELEASED' || data?.state === 'COLLECTED';
  const reached = data?.checkpoints.filter((c) => c.reached).length ?? 0;
  const total = data?.checkpoints.length ?? 1;
  const progress = Math.min(1, Math.max(0.02, reached / total));

  // 5 route nodes + the parcel marker along the arc
  const nodes = [0, 0.25, 0.5, 0.75, 1].map(bezier);
  const marker = bezier(progress);
  const nodeLabels = data?.method === 'locker'
    ? ['Seller', 'Locker network', 'Sorting hub', 'Your locker', 'Collected']
    : ['Seller', 'Dispatched', 'In transit', 'Out for delivery', 'Delivered'];

  return (
    <div className="bg-cream min-h-screen">
      {/* Hero */}
      <section className="section-dark relative text-cream py-12 overflow-hidden" style={{ background: 'linear-gradient(135deg,#0F0F12,#1a1208 55%,#0F0F12)' }}>
        <div className="absolute inset-0 dotgrid-light bg-[length:18px_18px] opacity-15" />
        <div className="absolute inset-0 bg-gold-mesh opacity-30" />
        <div className="relative max-w-[880px] mx-auto px-6">
          <div className="stamp text-gold text-[10px]">SOUQLI PARCEL TRACKING</div>
          {isLoading ? (
            <h1 className="display text-3xl md:text-4xl mt-2">Loading your parcel…</h1>
          ) : isError || !data ? (
            <h1 className="display text-3xl md:text-4xl mt-2">Parcel not found</h1>
          ) : (
            <>
              <h1 className="display text-3xl md:text-4xl mt-2 leading-tight">{data.brand} {data.productTitle}</h1>
              <div className="text-cream/60 text-sm mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                <span>{CARRIER_ICON[data.carrier] ?? '📦'} {data.carrierLabel}</span>
                {data.trackingNumber && <span className="mono text-cream/45">· {data.trackingNumber}</span>}
                <span className="mono text-cream/45">· {data.reference}</span>
              </div>
            </>
          )}
        </div>
      </section>

      {isError || (!isLoading && !data) ? (
        <div className="max-w-[880px] mx-auto px-6 py-16 text-center">
          <div className="text-5xl mb-3">🔍</div>
          <p className="text-mute">We couldn't find a parcel for tracking number <span className="mono text-ink">{code}</span>.</p>
          <Link to="/" className="btn btn-primary mt-6">Back to Souqli</Link>
        </div>
      ) : data ? (
        <div className="max-w-[880px] mx-auto px-6 py-10 space-y-8">
          {/* ETA + escrow badge - the Souqli difference, front and centre */}
          <div className="grid md:grid-cols-[1fr_260px] gap-4">
            <div className={`rounded-bold p-6 flex items-center gap-5 ${done ? 'bg-ok/10 border border-ok/30' : 'bg-white border border-warmline shadow-luxe'}`}>
              <div className="text-4xl">{done ? '✅' : '🚚'}</div>
              <div>
                {done ? (
                  <><div className="stamp text-ok text-[9px]">ARRIVED</div><div className="display text-2xl text-ink leading-tight">Your item has arrived</div></>
                ) : (
                  <><div className="stamp text-gold-dark text-[9px]">ESTIMATED {data.method === 'locker' ? 'READY TO COLLECT' : 'DELIVERY'}</div><div className="display text-2xl text-ink leading-tight">{fmtDate(data.estimatedDelivery) ?? `Within ${data.eta}`}</div></>
                )}
              </div>
            </div>
            <div className="rounded-bold p-5 text-cream relative overflow-hidden" style={{ background: 'linear-gradient(135deg,#D98A2B,#A66018)' }}>
              <div className="absolute inset-0 dotgrid-light bg-[length:14px_14px] opacity-20" />
              <div className="relative">
                <div className="text-2xl">🔒</div>
                <div className="display text-ink text-lg leading-tight mt-1">Your money is safe</div>
                <p className="text-ink/70 text-[11px] mt-1">Held safely by Souqli until you confirm you've collected. Not paid to the seller yet.</p>
              </div>
            </div>
          </div>

          {/* Custom animated route map - distinctly Souqli */}
          <div className="rounded-bold border border-warmline bg-white shadow-luxe p-5 overflow-hidden">
            <div className="stamp text-gold-dark text-[9px] mb-1">THE JOURNEY</div>
            <svg viewBox="0 0 620 160" className="w-full">
              {/* full route */}
              <path d={`M ${bezier(0).x} ${bezier(0).y} Q 300 18 ${bezier(1).x} ${bezier(1).y}`} fill="none" stroke="#E8E0CF" strokeWidth="3" strokeDasharray="2 7" strokeLinecap="round" />
              {/* travelled portion */}
              <path d={`M ${bezier(0).x} ${bezier(0).y} Q ${55 + (300 - 55) * Math.min(1, progress * 1.4)} ${128 + (18 - 128) * Math.min(1, progress * 1.4)} ${marker.x} ${marker.y}`} fill="none" stroke="#D98A2B" strokeWidth="3.5" strokeLinecap="round" />
              {nodes.map((nd, i) => {
                const nodeReached = progress >= i / 4 - 0.001;
                return (
                  <g key={i}>
                    <circle cx={nd.x} cy={nd.y} r={7} fill={nodeReached ? '#D98A2B' : '#FFF'} stroke={nodeReached ? '#A66018' : '#E8E0CF'} strokeWidth="2" />
                    <text x={nd.x} y={nd.y + 26} textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontSize="8.5" fill="#9A9AA3">{nodeLabels[i]}</text>
                  </g>
                );
              })}
              {/* parcel marker */}
              <g style={{ transition: 'transform 0.6s ease' }}>
                <circle cx={marker.x} cy={marker.y} r={14} fill="#0F0F12" />
                <text x={marker.x} y={marker.y + 5} textAnchor="middle" fontSize="15">{done ? '🎉' : '📦'}</text>
              </g>
            </svg>
          </div>

          {/* Granular checkpoints */}
          <div className="rounded-bold border border-warmline bg-white shadow-luxe p-6">
            <div className="stamp text-gold-dark text-[9px] mb-4">CHECKPOINTS</div>
            <ol className="relative border-l-2 border-warmline ml-2 space-y-5">
              {data.checkpoints.map((c) => (
                <li key={c.key} className="ml-5 relative">
                  <span className={`absolute -left-[26px] top-0 w-4 h-4 rounded-full grid place-items-center text-[9px] ${c.reached ? 'bg-ok text-cream' : c.current ? 'bg-gold text-ink animate-pulse' : 'bg-cream-deep border border-warmline'}`}>{c.reached ? '✓' : ''}</span>
                  <div className={`text-sm ${c.current ? 'font-semibold text-ink' : c.reached ? 'text-ink' : 'text-mute'}`}>{c.label}</div>
                  <div className="text-[12px] text-mute">{c.location}{c.at && <span className="mono"> · {fmtTime(c.at)}</span>}</div>
                </li>
              ))}
            </ol>
          </div>

          {/* Why Souqli tracking is different - the standout intro */}
          <div className="section-dark rounded-bold p-6 text-cream relative overflow-hidden" style={{ background: 'linear-gradient(135deg,#0F0F12,#1a1208 60%,#0F0F12)' }}>
            <div className="absolute inset-0 dotgrid-light bg-[length:18px_18px] opacity-15" />
            <div className="relative">
              <div className="stamp text-gold text-[10px]">WHY SOUQLI TRACKING IS DIFFERENT</div>
              <h2 className="display text-2xl mt-2">Tracking that protects you, not just informs you.</h2>
              <div className="grid sm:grid-cols-3 gap-4 mt-5">
                {[
                  ['🔒', 'Your money, held safe', 'Every other tracker shows where your parcel is. Ours also shows your money is held safe - released only when you confirm collection.'],
                  ['🪪', 'Verified, not anonymous', 'Every seller is ID-checked and every item authenticated before it ships. The tracking starts from a real, accountable person.'],
                  ['📦', 'Door-to-locker certainty', 'A one-time PIN, condition photos at every hand-off, and a 48-hour window to raise anything - end to end, with proof.'],
                ].map(([icon, t, b]) => (
                  <div key={t as string} className="border-l border-gold/30 pl-3">
                    <div className="text-xl">{icon}</div>
                    <div className="text-sm font-medium mt-1.5">{t}</div>
                    <p className="text-cream/60 text-[12px] mt-1 leading-relaxed">{b}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <p className="text-center text-[11px] text-mute">Share this link - anyone with it can follow the parcel, but never your details. <Link to="/" className="text-gold-dark hover:underline">Souqli</Link></p>
        </div>
      ) : null}
    </div>
  );
}
